/**
 * The trash: what a delete keeps, for how long, and who may take it back. Pure — no clock,
 * no database, no bucket. The handlers and the sweeper pass `now` in.
 *
 * A delete hides a material at once and keeps its bytes for `TRASH_RETENTION_DAYS`. Inside
 * that window the uploader or a group admin may restore it, or delete it for good. After the
 * window the sweeper purges it. Before this, a mis-click lost the file for good.
 *
 * Three states, read off two columns (`migrations/0003_material_trash.sql`):
 *
 * - **in the group** — `deleted_at` null.
 * - **trashed** — `deleted_at` set, `purged_at` null. Hidden, bytes kept, restorable.
 * - **purged** — both set. The bytes are gone or going; the row stays so the comment
 *   thread stays readable (R14). Nothing brings a purged row back.
 */

export const TRASH_RETENTION_DAYS = 30;
export const TRASH_RETENTION_MS = TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The last instant a trashed material can be restored, exclusive. ISO, like every stamp here. */
export const restorableUntil = (deletedAt: string): string =>
  new Date(Date.parse(deletedAt) + TRASH_RETENTION_MS).toISOString();

/** A row deleted before this instant is past its window. The sweeper's `WHERE` reads it. */
export const purgeCutoff = (now: string): string =>
  new Date(Date.parse(now) - TRASH_RETENTION_MS).toISOString();

/** Whole days left before the window closes, rounded up. 0 means it closes today. */
export const daysLeft = (deletedAt: string, now: string): number => {
  const left = Date.parse(restorableUntil(deletedAt)) - Date.parse(now);
  return left <= 0 ? 0 : Math.ceil(left / DAY_MS);
};

export interface TrashRow {
  readonly uploadedBy: string;
  readonly deletedAt: string | null;
  readonly purgedAt: string | null;
  readonly sizeBytes: number;
}

export interface TrashActor {
  readonly userId: string;
  readonly isAdmin: boolean;
}

/** The same rule delete uses: the uploader may manage their own, an admin may manage any. */
export const mayManageTrash = (row: Pick<TrashRow, 'uploadedBy'>, actor: TrashActor): boolean =>
  actor.isAdmin || row.uploadedBy === actor.userId;

const isTrashed = (row: TrashRow): boolean => row.deletedAt !== null && row.purgedAt === null;

/**
 * What the group already holds, not counting the row being restored, and its walls.
 *
 * A trashed row already counts toward the caps, so a restore normally fits. The check is
 * still made, because the walls are refusals and a restore is a write.
 */
export interface GroupUsage {
  readonly materials: number;
  readonly bytes: number;
}

export type RestoreRefusal = 'not_trashed' | 'expired' | 'not_permitted' | 'group_full';
export type PurgeRefusal = 'not_trashed' | 'not_permitted';

export type TrashDecision<R> = { ok: true } | { ok: false; reason: R };

/**
 * Whether a restore may go ahead. The order of the checks picks the answer: a row that is
 * not in the trash, or whose window has closed, is gone for everyone; only then does it
 * matter who is asking; only then whether it fits.
 */
export const decideRestore = (input: {
  row: TrashRow;
  actor: TrashActor;
  now: string;
  usage: GroupUsage;
  caps: GroupUsage;
}): TrashDecision<RestoreRefusal> => {
  const { row, actor, now, usage, caps } = input;
  if (!isTrashed(row)) return { ok: false, reason: 'not_trashed' };
  if (Date.parse(now) >= Date.parse(restorableUntil(row.deletedAt as string))) {
    return { ok: false, reason: 'expired' };
  }
  if (!mayManageTrash(row, actor)) return { ok: false, reason: 'not_permitted' };
  if (usage.materials + 1 > caps.materials || usage.bytes + row.sizeBytes > caps.bytes) {
    return { ok: false, reason: 'group_full' };
  }
  return { ok: true };
};

/** "Delete for good". Any trashed row, inside its window or past it, for the same two people. */
export const decidePurge = (input: { row: TrashRow; actor: TrashActor }): TrashDecision<PurgeRefusal> => {
  if (!isTrashed(input.row)) return { ok: false, reason: 'not_trashed' };
  if (!mayManageTrash(input.row, input.actor)) return { ok: false, reason: 'not_permitted' };
  return { ok: true };
};
